import { formatDeviceName } from '../util/deviceName';
import { CLOSE_SESSION, UPDATE_CUSTOMER_LABELS, UPDATE_DEVICE_LOCATIONS, UPDATE_WEIGHTS } from './sessionMonitor.action';
import {
    ADD_BASKET_CHANGE,
    ADD_IMAGE,
    ADD_TARA_CHANGE,
    ADD_TICKET_CHANGE,
    PAYMENT_REQUEST_CHANGED,
    PAYMENT_REQUEST_CREATED,
    SESSION_MANUAL_CHECK,
    SESSION_SCALE_TRIGGER,
    SESSION_WEIGHT_DEVIATION,
    SESSION_WEIGHT_WITHOUT_SCAN,
    SET_SESSION_LOADING,
    UPDATE_SESSION_COMPLETE,
    UPDATE_SESSION_FAILED,
} from './sessionMonitorDetails.action';
import assertType from '@whiz-cart/node-shared/assertType';
import type { CustomerLabel } from '@whiz-cart/node-shared/models/customer/customerLabel';
import type { WeightWithoutScanImagesModel } from '@whiz-cart/node-shared/models/session/changes';
import type { Session } from '@whiz-cart/node-shared/models/session/session';
import { type CartImageView, cartImageViewUrl } from '@whiz-cart/node-shared/models/snapshot/cartImageView';
import _, { mapValues } from 'lodash';

export interface SessionMonitorDetailsSession extends Session {
    history?: any[];
}

const processBasketChange = (item: any) => {
    const {
        delta,
        basketItem: { basketItemGuid },
        modifiedOn,
        createdOn,
        changeGuid,
    } = item;
    if (delta === 0) return [];

    return {
        ...item,
        id: `${basketItemGuid}:${changeGuid}`,
        type: 'basketChange',
        time: modifiedOn ?? createdOn,
        receivedOn: new Date(),
        images: item.basketItem?.basketMetadataList?.find((x: any) => x.correlationGuid === item.changeGuid)?.images,
    };
};

const processTicketChange = (item: any) => {
    const { closedOn, id, openedOn } = item;

    const history = [];
    history.unshift({
        ...item,
        status: 0,
        historyId: `${id}:opened`,
        type: 'ticketChange',
        time: openedOn,
    });

    if (closedOn) {
        history.unshift({
            ...item,
            historyId: `${id}:closed`,
            type: 'ticketChange',
            time: closedOn,
        });
    }

    return history;
};

const processTaraChange = (item: any) => {
    const { createdOn } = item;

    return {
        ...item,
        id: `tara:${createdOn}`,
        type: 'tara',
        time: createdOn,
    };
};

const processScaleTrigger = (item: any) => {
    const { createdOn } = item;

    return {
        ...item,
        id: `scaleTrigger:${createdOn}`,
        type: 'scaleTrigger',
        time: createdOn,
    };
};

/**
 * @param images Resolved image URLs for this alert. Store-manager resolves them — on the live notification as
 * `payload.images`, and on a fetched session in the session-changes response — so this screen never builds a cart
 * image URL and needs no knowledge of the storage generation. Undefined when the alert stored no images, or when
 * the store's session-monitor snapshot gate suppressed them.
 */
const processWeightWithoutScan = (item: any, images?: WeightWithoutScanImagesModel) => {
    return {
        ...item,
        ...item.model,
        snapshotUrl: images?.snapshotUrl,
        snapshotBeforeEventUrl: images?.snapshotBeforeEventUrl,
        id: `weightWithoutScan:${item?.model?.createdOn ?? item?.createdOn}`,
        type: 'weightWithoutScan',
        time: item?.model?.createdOn ?? item?.createdOn,
    };
};

const processManualCheck = (item: any) => {
    return item;
};

const processCustomerMark = (item: any) => {
    return item;
};

const processWeightDeviation = (item: any) => {
    const { createdOn } = item;

    return {
        ...item,
        id: `weightDeviation:${createdOn}`,
        type: 'weightDeviation',
        time: createdOn,
    };
};

const processSessionEvents = (session: any) => {
    const items = [];

    items.push({
        ...session,
        id: 'sessionStart',
        type: 'sessionStart',
        time: session.startedOn,
        ...session.history.sessionStartImages,
    });

    if (session.endedOn) {
        items.push({
            ...session,
            id: 'sessionEnd',
            type: 'sessionEnd',
            time: session.endedOn,
            ...session.history.sessionEndImages,
        });
    }

    for (const paymentRequest of session.history.paymentRequests) {
        items.push({
            ...paymentRequest,
            id: `${paymentRequest.paymentRequestGuid}:start`,
            type: 'paymentStart',
            time: paymentRequest.createdOn,
        });

        if (paymentRequest.completedOn || paymentRequest.status > 2) {
            items.push({
                ...paymentRequest,
                id: `${paymentRequest.paymentRequestGuid}:end`,
                type: 'paymentEnd',
                time: paymentRequest.completedOn ?? paymentRequest.modifiedOn,
            });
        }
    }

    return items;
};

const processAsyncAvailableImages = (asyncAvailableImages: any, image: CartImageView) => {
    const result = { ...asyncAvailableImages };
    result[image.logicalPath] = cartImageViewUrl(image);
    return result;
};

const update = (state: any, sessionGuid: any, update: any, upsert: any) => {
    if (!state[sessionGuid] && !upsert) return state;

    const session = update(state[sessionGuid]);

    session.deviceId = formatDeviceName(session.isMobile ? String(session.sequence) : session.cartId, session.isMobile);

    session.history = _(session.history)
        .orderBy('receivedOn', 'desc')
        .uniqBy('id')
        .orderBy('time', 'desc')
        .map((item) => {
            if (item.images) {
                const availableImages = _.uniq(
                    (item.availableImages ?? []).concat(
                        item.images.map((path: any) => session.asyncAvailableImages?.[path]).filter(Boolean),
                    ),
                );

                item = { ...item, availableImages };
            }

            return item;
        })
        .value();

    session.items = _(session.history)
        .filter((item) => item.type === 'basketChange')
        .orderBy('time', 'desc')
        .map('basketItem')
        .uniqBy('basketItemGuid')
        .orderBy('createdOn', 'desc')
        .value();

    return { ...state, [sessionGuid]: session };
};

export default (state: Record<string, SessionMonitorDetailsSession> = {}, { type, payload }: { type: string; payload: any }) => {
    switch (type) {
        case SET_SESSION_LOADING:
            return { ...state, [payload.sessionGuid]: { ...state[payload.sessionGuid], isLoading: payload.loading } };

        case UPDATE_SESSION_COMPLETE: {
            const resolvedWwsImages = new Map<string, WeightWithoutScanImagesModel>(
                (payload.history.weightWithoutScanAlerts ?? []).map((images: WeightWithoutScanImagesModel) => [images.modelGuid, images]),
            );
            const history = [].concat(
                ...payload.history.basketChanges.map(processBasketChange),
                ...payload.history.tickets.map(processTicketChange),
                ...payload.history.taras.map(processTaraChange),
                ...(payload.history.scaleTriggers?.map(processScaleTrigger) ?? []),
                ...(payload.weightWithoutScanAlerts?.map((alert: any) =>
                    processWeightWithoutScan(alert, resolvedWwsImages.get(alert?.modelGuid)),
                ) ?? []),
                ...(payload.history.weightDeviations?.map(processWeightDeviation) ?? []),
                ...processSessionEvents(payload),
            );
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    ...payload,
                    history: !processCustomerMark(payload.customer)?.labels?.includes('IsSuspicious')
                        ? history?.filter((t: any) => t?.category !== 'taggedCustomer')
                        : history,
                    manualCheck: processManualCheck(payload.manualCheck),
                    customer: processCustomerMark(payload.customer),
                    tickets: !processCustomerMark(payload.customer)?.labels?.includes('IsSuspicious')
                        ? payload?.history?.tickets?.filter((t: any) => t?.category !== 'taggedCustomer')
                        : payload?.history?.tickets,
                    isLoading: false,
                }),
                true,
            );
        }

        case UPDATE_SESSION_FAILED:
            return { state, [payload.sessionGuid]: { ...payload, isLoading: false } };

        case ADD_BASKET_CHANGE:
            return update(
                state,
                payload.basketItem.sessionGuid,
                (s: any) => {
                    if (payload.oldAmount === null && payload.newAmount === 0) {
                        // item is unknown barcode that was automatically removed by the system
                        // we look for an entry with the same changeGuid and remove it, since a separate "unknown barcode" event is generated
                        // if the user decides to keep the unknown barcode item, a new entry will be generated
                        const history = [...s.history];
                        const index = history.findIndex((entry) => entry.changeGuid === payload.changeGuid);
                        if (index !== -1) {
                            history.splice(index, 1);
                            return {
                                ...s,
                                history,
                            };
                        }
                    }

                    return {
                        ...s,
                        history: s.history.concat(processBasketChange(payload)),
                    };
                },
                undefined,
            );

        case ADD_TICKET_CHANGE:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    history: s.history.concat(processTicketChange(payload)),
                    tickets: s.tickets.filter((t: any) => t.id !== payload.id).concat(payload),
                }),
                undefined,
            );

        case ADD_TARA_CHANGE:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    history: s.history.concat(processTaraChange(payload.tara)),
                }),
                undefined,
            );

        case ADD_IMAGE:
            return update(
                state,
                // -compat-(claude)- The condensed payload has no `properties` bag; `sessionGuid` is top-level and
                //                   was present in the previous, fat payload too, so this reads both.
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    asyncAvailableImages: processAsyncAvailableImages(s.asyncAvailableImages, payload),
                }),
                undefined,
            );

        case SESSION_SCALE_TRIGGER:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    history: s.history.concat(processScaleTrigger(payload)),
                }),
                undefined,
            );

        case SESSION_WEIGHT_WITHOUT_SCAN:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    history: s.history.concat(processWeightWithoutScan(payload, payload.images)),
                }),
                undefined,
            );

        case SESSION_MANUAL_CHECK:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    manualCheck: processManualCheck(payload),
                }),
                undefined,
            );

        case UPDATE_CUSTOMER_LABELS: {
            assertType<{ customerGuid: string; labels: CustomerLabel[] }>(payload);
            const labels = payload.labels.filter((label) => !label.deletedOn).map((label) => label.label);
            const isSuspicious = labels.includes('IsSuspicious');

            return mapValues(state, (session) => {
                if (session.customer?.userGuid !== payload.customerGuid) {
                    return session;
                }

                return {
                    ...session,
                    customer: {
                        ...session.customer,
                        labels,
                    },
                    history: !isSuspicious ? session.history?.filter((h: any) => h?.category !== 'taggedCustomer') : session.history,
                    tickets: !isSuspicious ? session.tickets?.filter((t) => t?.category !== 'taggedCustomer') : session.tickets,
                };
            });
        }

        case SESSION_WEIGHT_DEVIATION:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    history: s.history.concat(processWeightDeviation(payload)),
                }),
                undefined,
            );

        case UPDATE_WEIGHTS:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => {
                    if (s.weightUpdatedOn > payload.t) return s;

                    return {
                        ...s,
                        actualWeight: payload.actualWeight ?? s.actualWeight,
                        expectedWeight: payload.expectedWeight ?? s.expectedWeight,
                        weightUpdatedOn: payload.t,
                    };
                },
                undefined,
            );

        case CLOSE_SESSION:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    endedOn: payload.endedOn,
                    history: s.history.concat({
                        id: 'sessionEnd',
                        type: 'sessionEnd',
                        time: payload.endedOn,
                        images: payload.images,
                    }),
                }),
                undefined,
            );

        case PAYMENT_REQUEST_CREATED:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    paymentRequests: s.paymentRequests.concat(payload),
                    history: s.history.concat({
                        ...payload,
                        id: `${payload.paymentRequestGuid}:start`,
                        type: 'paymentStart',
                        time: payload.createdOn,
                    }),
                }),
                undefined,
            );

        case PAYMENT_REQUEST_CHANGED:
            return update(
                state,
                payload.sessionGuid,
                (s: any) => ({
                    ...s,
                    paymentRequests: s.paymentRequests
                        .filter((x: any) => x.paymentRequestGuid !== payload.paymentRequestGuid)
                        .concat(payload),
                    history: s.history.concat({
                        ...payload,
                        id: `${payload.paymentRequestGuid}:end`,
                        type: 'paymentEnd',
                        time: payload.completedOn ?? payload.modifiedOn,
                    }),
                }),
                undefined,
            );

        case UPDATE_DEVICE_LOCATIONS:
            return payload.reduce(
                (state: any, { sessionGuid, location }: any) =>
                    update(
                        state,
                        sessionGuid,
                        (s: any) => ({
                            ...s,
                            location,
                        }),
                        undefined,
                    ),
                state,
            );

        default:
            return state;
    }
};
